import _ from 'lodash';
import moment from 'moment-timezone';
import { SLOT_DURATION, SLOTS_IN_DAY, SLOTS_IN_HOUR, } from 'ziphy-web-shared/basic/entities/schedules';
import { isLocal, toNumberIfPossible } from 'ziphy-web-shared/basic/helpers';
import { getIsoDate } from 'ziphy-web-shared/basic/utils/time';
import { EXTRA_SLOTS, MAX_COLUMN_WIDTH } from './Calendar.constants';
import { $calendar } from './Calendar.store';
/*
 * SLOTS MAPPING
 */
class SlotsMapping {
    get mapping() {
        const begin = moment.utc().startOf('day');
        return Object.fromEntries(Array.from({ length: SLOTS_IN_DAY }, (_, index) => {
            const key = begin
                .clone()
                .add(SLOT_DURATION * index, 'minute')
                .format('HH:mm:ss');
            return [key, index];
        }));
    }
    getRowIndex(date) {
        var _a;
        const key = (_a = moment.utc(date).tz($calendar.timezone)) === null || _a === void 0 ? void 0 : _a.format('HH:mm:ss');
        return this.mapping[key];
    }
    getHour(index) {
        var _a;
        const found = Object.entries(this.mapping).find(([key, x]) => x === index);
        return Number(_.trimStart((_a = found === null || found === void 0 ? void 0 : found[0]) === null || _a === void 0 ? void 0 : _a.substring(0, 2), '0')) || 0;
    }
    getRowIndexes(_begin, _end) {
        const result = {};
        let begin = moment(_begin);
        _end = moment(_end);
        while (begin.isBefore(_end)) {
            const date = getIsoDate(begin, $calendar.timezone);
            result[date] = result[date] || [];
            result[date].push(this.getRowIndex(begin));
            begin.add(SLOT_DURATION, 'minute');
        }
        return Object.entries(result);
    }
}
export const slotsMapping = new SlotsMapping();
/*
 * GENERATE AND FILLING SLOTS
 */
export function generateDays(_begin, _end, { timezone }) {
    const result = [];
    let begin = moment(_begin);
    _end = moment(_end);
    while (begin.isBefore(_end)) {
        result.push(getIsoDate(begin, timezone));
        begin.add(1, 'day');
    }
    return result;
}
export function generateSlots(_begin, _end, { appointments, travels, shifts, offices, timezone, }) {
    const result = {};
    let begin = moment(_begin);
    _end = moment(_end);
    while (begin.isBefore(_end)) {
        const date = getIsoDate(begin, timezone);
        result[date] = result[date] || [];
        const slot = {
            index: result[date].length,
            begin: begin,
            end: begin.clone().add(SLOT_DURATION, 'minute'),
            shiftIds: [],
            officeIds: [],
            practiceIds: [],
            travelIds: [],
            appointmentIds: [],
        };
        result[date].push(slot);
        begin = slot.end.clone();
    }
    if (shifts)
        fillWithShifts(result, shifts, offices);
    if (travels)
        fillWithTravels(result, travels);
    if (appointments)
        fillWithAppointments(result, appointments);
    return result;
}
export function fillWithShifts(slots, items, offices) {
    const shifts = _.sortBy(Object.values(items), 'begin');
    const availableTimes = [];
    shifts.forEach(({ begin, end, id, availableTime }) => {
        slotsMapping.getRowIndexes(begin, end).forEach(([date, indexes]) => {
            indexes.forEach((index) => {
                var _a;
                if ((_a = slots[date]) === null || _a === void 0 ? void 0 : _a[index])
                    slots[date][index].shiftIds.push(id);
            });
            if (availableTime) {
                Object.entries(availableTime).forEach(([officeId, item]) => {
                    item.forEach((x) => availableTimes.push(Object.assign({ officeId: Number(officeId) }, x)));
                });
            }
        });
    });
    // Fill Offices and Practices
    _.sortBy(availableTimes, 'begin').forEach(({ begin, end, officeId }) => {
        slotsMapping.getRowIndexes(begin, end).forEach(([date, indexes]) => {
            indexes.forEach((index) => {
                var _a, _b;
                if ((_a = slots[date]) === null || _a === void 0 ? void 0 : _a[index]) {
                    const officesSet = new Set([...slots[date][index].officeIds, officeId]);
                    slots[date][index].officeIds = Array.from(officesSet);
                    const practiceId = (_b = offices === null || offices === void 0 ? void 0 : offices[officeId]) === null || _b === void 0 ? void 0 : _b.practiceId;
                    if (practiceId) {
                        const practicesSet = new Set([...slots[date][index].practiceIds, practiceId]);
                        slots[date][index].practiceIds = Array.from(practicesSet);
                    }
                }
            });
        });
    });
}
export function fillWithTravels(slots, items) {
    const travels = _.sortBy(items, 'appointedStart');
    travels.forEach(({ appointedStart: begin, appointedFinish: end, id }) => {
        slotsMapping.getRowIndexes(begin, end).forEach(([date, indexes]) => {
            indexes.forEach((index) => {
                var _a;
                if ((_a = slots[date]) === null || _a === void 0 ? void 0 : _a[index])
                    slots[date][index].travelIds.push(id);
            });
        });
    });
    return slots;
}
export function fillWithAppointments(slots, items) {
    const appointments = _.orderBy(items, ['appointedStart', 'providerFraction']);
    appointments.forEach(({ appointedStart: begin, appointedFinish: end, id }) => {
        slotsMapping.getRowIndexes(begin, end).forEach(([date, indexes]) => {
            const tmp = [];
            indexes.forEach((index) => {
                var _a, _b, _c;
                tmp.push(((_c = (_b = (_a = slots[date]) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.appointmentIds) === null || _c === void 0 ? void 0 : _c.length) || 0);
            });
            const currentIndex = Math.max(...tmp, 0);
            indexes.forEach((index) => {
                var _a;
                if ((_a = slots[date]) === null || _a === void 0 ? void 0 : _a[index])
                    slots[date][index].appointmentIds[currentIndex] = id;
            });
        });
    });
    return slots;
}
/*
 * OTHER
 */
export function getScheduleDayTemplate() {
    return {
        workingTime: {
            begin: null,
            end: null,
        },
        allAppointmentIds: new Set(),
        maxCols: MAX_COLUMN_WIDTH,
        cards: {},
    };
}
export function findTimeLimits(schedule) {
    let startIndex = null;
    let endIndex = null;
    Object.values(schedule).forEach((value) => {
        const cards = Object.values(value.cards);
        if (cards.length > 0) {
            const min = Math.min(...cards.map((x) => x.slotIndex));
            const max = Math.max(...cards.map((x) => x.slotIndex + x.height));
            if (startIndex === null || min < startIndex)
                startIndex = min;
            if (endIndex === null || max > endIndex)
                endIndex = max;
        }
    });
    if (startIndex === null) {
        startIndex = 8 * SLOTS_IN_HOUR;
    }
    else {
        startIndex = Math.max(startIndex - EXTRA_SLOTS, 0);
    }
    if (endIndex === null) {
        endIndex = 18 * SLOTS_IN_HOUR;
    }
    else {
        endIndex = Math.min(endIndex + EXTRA_SLOTS, SLOTS_IN_DAY);
    }
    return { startIndex, endIndex };
}
/*
 * DEV
 */
const useDev = false;
const logDates = [
    '2024-11-30', //
];
export const DevSlotsLog = (slots) => {
    if (!isLocal() || !useDev)
        return;
    return _.forEach(_.cloneDeep(slots), (slots, date) => {
        if (logDates.length && !logDates.includes(date))
            return;
        console.groupCollapsed('slots:', date);
        const table = _.mapValues(slots, (slot) => _.mapValues(_.omit(slot, ['end']), (x, k) => {
            if (moment.isMoment(x))
                x = x.clone().format('HH:mm');
            if (Array.isArray(x))
                x = x.filter(Boolean).join(', ');
            return toNumberIfPossible(x) || null;
        }));
        console.table(table);
        console.groupEnd();
    });
};
export const DevScheduleLog = (schedule) => {
    if (!isLocal() || !useDev)
        return;
    return _.forEach(_.cloneDeep(schedule), (scheduleDay, date) => {
        if (logDates.length && !logDates.includes(date))
            return;
        console.groupCollapsed(`schedule: ${date}, maxCols: ${scheduleDay.maxCols}`);
        const table = _.mapValues(scheduleDay.cards, (card) => _.mapValues(_.omit(card, ['']), (x, k) => {
            if (moment.isMoment(x))
                x = x.clone().format('HH:mm');
            if (Array.isArray(x))
                x = x.filter(Boolean).join(', ');
            return toNumberIfPossible(x) || null;
        }));
        console.table(table);
        console.groupEnd();
    });
};
